import { useEffect, useMemo, useRef, useState } from "react";
import "./CriarPrimaria.css";

const UP = (v) => (typeof v === "string" ? v.toUpperCase() : v ?? "");
const ATIVO_OPTIONS = [
  { value: 1, label: "SIM" },
  { value: 0, label: "NÃO" },
];

function CriarPrimariaDropdown({ label, name, value, options, onSelect, placeholder = "SELECIONE" }) {
  const [open, setOpen] = useState(false);
  const [renderMenu, setRenderMenu] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = useState(220);
  const wrapRef = useRef(null);
  const closeTimerRef = useRef(null);
  const itemHeightRef = 44;

  const current = useMemo(() => options.find((o) => o.value === value) || null, [options, value]);

  useEffect(() => {
    function onDocDown(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) closeMenu();
    }
    document.addEventListener("mousedown", onDocDown);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const onWindowChange = () => {
      updatePlacement();
    };

    window.addEventListener("resize", onWindowChange);
    window.addEventListener("scroll", onWindowChange, true);
    return () => {
      window.removeEventListener("resize", onWindowChange);
      window.removeEventListener("scroll", onWindowChange, true);
    };
  }, [open, options.length]);

  function updatePlacement() {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const modal = wrapRef.current.closest(".dvpr-box");
    const modalRect = modal?.getBoundingClientRect();

    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const safePad = 12;

    const viewportBelow = Math.max(0, viewportH - rect.bottom - safePad);
    const viewportAbove = Math.max(0, rect.top - safePad);
    const modalBelow = modalRect ? Math.max(0, modalRect.bottom - rect.bottom - safePad) : Number.POSITIVE_INFINITY;
    const modalAbove = modalRect ? Math.max(0, rect.top - modalRect.top - safePad) : Number.POSITIVE_INFINITY;

    const spaceBelow = Math.max(0, Math.min(viewportBelow, modalBelow));
    const spaceAbove = Math.max(0, Math.min(viewportAbove, modalAbove));

    const target = Math.min(320, options.length * itemHeightRef + 12);
    const shouldOpenUp = spaceBelow < 190 && spaceAbove > spaceBelow;
    const available = shouldOpenUp ? spaceAbove : spaceBelow;
    const dynamicMax = Math.max(96, Math.min(target, available - 6));

    setOpenUp(shouldOpenUp);
    setMenuMaxHeight(dynamicMax);
  }

  const openMenu = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setRenderMenu(true);
    requestAnimationFrame(() => {
      updatePlacement();
      setOpen(true);
    });
  };

  const closeMenu = () => {
    setOpen(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setRenderMenu(false), 240);
  };

  const toggle = () => {
    if (open) closeMenu();
    else openMenu();
  };

  return (
    <div ref={wrapRef} className={`dvpr-field dvpr-field-select ${open ? "is-open" : ""} ${openUp ? "is-open-up" : ""}`}>
      <label className="dvpr-label">{label}</label>
      <div className="dvpr-pick-anchor">
        <button
          type="button"
          className="dvpr-pick-btn"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={toggle}
        >
          <span className={`dvpr-pick-val ${current ? "" : "is-placeholder"}`}>
            {current ? current.label : placeholder}
          </span>
        </button>

        {renderMenu && (
          <div
            className={`dvpr-pick-menu ${open ? "is-open" : "is-closing"}`}
            role="listbox"
            aria-label={label}
            style={{ maxHeight: `${menuMaxHeight}px` }}
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="dvpr-pick-opt"
                onClick={() => {
                  onSelect(name, opt.value);
                  closeMenu();
                }}
                role="option"
                aria-selected={opt.value === value}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CriarPrimaria({ open, contexto, onClose, onConfirm, onSalvar }) {
  const submit = onConfirm || onSalvar || (() => {});

  const [form, setForm] = useState({
    causador: contexto?.causador || "",
    causa_primaria: "",
    causa_secundaria: "",
    quando_utilizado: "",
    ativo: "",
  });

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setForm({
        causador: UP(contexto?.causador || ""),
        causa_primaria: "",
        causa_secundaria: "",
        quando_utilizado: "",
        ativo: "",
      });
    }
  }, [open, contexto]);

  if (!open) return null;

  return (
    <div className="dvpr-bg">
      <div className="dvpr-box" role="dialog" aria-modal="true" aria-label="Cadastrar primaria">
        <header className="dvpr-head">
          <h3 className="dvpr-title">Cadastrar causa primaria</h3>
          <button className="dvpr-x" type="button" onClick={onClose} aria-label="Fechar">x</button>
        </header>

        <div className="dvpr-body">
          <div className="dvpr-row">
            <div className="dvpr-field">
              <label className="dvpr-label">Causador</label>
              <input className="dvpr-input" value={form.causador} readOnly disabled />
            </div>
            <div className="dvpr-field">
              <label className="dvpr-label">Causa Primaria</label>
              <input
                className="dvpr-input"
                value={form.causa_primaria}
                onChange={(e) => setForm((s) => ({ ...s, causa_primaria: UP(e.target.value) }))}
                placeholder="EX.: PROCESSO"
              />
            </div>
          </div>

          <div className="dvpr-row">
            <div className="dvpr-field">
              <label className="dvpr-label">Causa Secundaria (primeira)</label>
              <input
                className="dvpr-input"
                value={form.causa_secundaria}
                onChange={(e) => setForm((s) => ({ ...s, causa_secundaria: UP(e.target.value) }))}
                placeholder="EX.: FALHA DE COMPONENTE"
              />
            </div>
            <CriarPrimariaDropdown
              label="Ativo"
              name="ativo"
              value={form.ativo}
              options={ATIVO_OPTIONS}
              onSelect={(name, nextValue) => setForm((s) => ({ ...s, [name]: Number(nextValue) }))}
            />
          </div>

          <div className="dvpr-row">
            <div className="dvpr-field dvpr-grow">
              <label className="dvpr-label">Quando Utilizado</label>
              <textarea
                className="dvpr-input"
                rows={3}
                value={form.quando_utilizado}
                onChange={(e) => setForm((s) => ({ ...s, quando_utilizado: e.target.value }))}
                placeholder="Descreva quando utilizar..."
              />
            </div>
          </div>
        </div>

        <footer className="dvpr-foot">
          <button className="dvpr-btn" type="button" onClick={onClose}>Cancelar</button>
          <button
            className="dvpr-btn dvpr-btn-ok"
            type="button"
            onClick={() => submit(form)}
            disabled={
              !form.causador ||
              !form.causa_primaria ||
              !form.causa_secundaria ||
              !form.quando_utilizado ||
              (form.ativo !== 0 && form.ativo !== 1)
            }
          >
            Salvar
          </button>
        </footer>
      </div>
    </div>
  );
}
